import React, { useMemo, useState } from "react";
import { View, StyleSheet, Pressable, Platform, GestureResponderEvent, StyleProp, ViewStyle } from "react-native";
import { Menu, ActivityIndicator } from "react-native-paper";
import CustomText from "../../common/general/CustomText/CustomText";
import { ThreeDot } from "../../svgs/common";
import { HeadCircuit, ArrowsClockwise, FileText, Link, Sparkle, FolderUser } from "@phosphor-icons/react";

const TYPE_CONFIG = {
  "Interactive Learning Experience": {
    Icon: Sparkle,
  },
  Resource: {
    Icon: Link,
  },
  Assignment: {
    Icon: FileText,
  },
  Assessment: {
    Icon: HeadCircuit,
  },
  Review: {
    Icon: ArrowsClockwise,
  },
  Project: {
    Icon: FolderUser,
  },
};

interface ActivityCardProps {
  title: string;
  type: string;
  item: any;
  onOptionsPress: (action: string) => void;
  style?: StyleProp<ViewStyle>;
  onLongPress: (event: GestureResponderEvent) => void;
}

const DragHandle = () => (
  <>
    <View style={styles.dragDots}>
      <View style={styles.dot} />
      <View style={styles.dot} />
    </View>
    <View style={styles.dragDots}>
      <View style={styles.dot} />
      <View style={styles.dot} />
    </View>
  </>
);

const ActivityCard = ({ title, type, item, onOptionsPress, style, onLongPress }: ActivityCardProps) => {
  const config = TYPE_CONFIG[type];
  const Icon = config.Icon;

  const [menuVisible, setMenuVisible] = useState(false);
  const [anchorPosition, setAnchorPosition] = useState({ x: 0, y: 0 });

  const openMenu = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const { pageX, pageY } = event.nativeEvent;
    setAnchorPosition({ x: pageX - 100, y: pageY });
    setMenuVisible(true);
  };

  const closeMenu = () => setMenuVisible(false);

  const handleOptionSelect = (action) => {
    if (onOptionsPress) {
      onOptionsPress(action);
    }
    closeMenu();
  };

  if (Platform.OS === "web") {
    window.addEventListener(
      "contextmenu",
      function (e) {
        e.preventDefault();
      },
      true
    );
  }

  const handleDragStart = (e) => {
    e.preventDefault();
    onLongPress(e);
  };

  const isInteractiveActivityPendingProcessing: boolean =
    type == "Interactive Learning Experience" &&
    Array.isArray(item?.objectives) &&
    item.objectives.some((objective) => objective.pendingVideoProcessing === true);

  return (
    <View style={[styles.container, style]}>
      <View style={styles.contentContainer}>
        <View style={styles.iconContainer}>{Icon && <Icon size={20} color="#667085" weight="bold" />}</View>
        <View style={styles.textContainer}>
          <CustomText
            text={type === "Assessment" ? "Lesson Assessment" : title}
            useTranslationText={false}
            weight="bold"
            style={styles.title}
            numberOfLines={1}
            ellipsizeMode="tail"
          />
          {type !== "Assessment" && (
            <CustomText
              text={type}
              useTranslationText={false}
              style={styles.subtitle}
              size="s"
              numberOfLines={1}
              weight="normal"
              ellipsizeMode="tail"
            />
          )}
        </View>
      </View>
      <View style={styles.actionsContainer}>
        {isInteractiveActivityPendingProcessing && <ActivityIndicator />}
        <Menu
          visible={menuVisible}
          onDismiss={closeMenu}
          anchor={{ x: anchorPosition.x, y: anchorPosition.y }}
          contentStyle={{ backgroundColor: "#FFFFFF", borderRadius: 5 }}>
          {type == "Interactive Learning Experience" && (
            <Menu.Item onPress={() => handleOptionSelect("clone")} title="Clone" titleStyle={{ color: "#344054" }} />
          )}
          <Menu.Item
            disabled={isInteractiveActivityPendingProcessing}
            onPress={() => handleOptionSelect("edit")}
            title="Edit"
            titleStyle={{ color: isInteractiveActivityPendingProcessing ? "#d4d4d4ff" : "#344054" }}
          />
          {type !== "Assessment" && (
            <Menu.Item onPress={() => handleOptionSelect("move")} title="Move" titleStyle={{ color: "#344054" }} />
          )}
          <Menu.Item onPress={() => handleOptionSelect("delete")} title="Delete" titleStyle={{ color: "#344054" }} />
        </Menu>
        <Pressable onPress={(e) => openMenu(e)} style={styles.optionsButton}>
          <ThreeDot fill="#000" width={20} height={20} />
        </Pressable>
      </View>
      <Pressable onPointerDown={handleDragStart} style={styles.dragHandle}>
        <DragHandle />
      </Pressable>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 12,
    paddingHorizontal: 20,
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#EAECF0",
    marginVertical: 4,
    width: "100%",
    position: "relative",
  },
  contentContainer: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    flex: 1,
  },
  iconContainer: {
    marginRight: 8,
  },
  textContainer: {
    flex: 1,
  },
  title: {
    color: "#344054",
    marginBottom: 2,
  },
  subtitle: {
    color: "#667085",
  },
  actionsContainer: {
    marginLeft: 12,
    flexDirection: "row",
    alignItems: "center",
  },
  optionsButton: {
    padding: 4,
    marginRight: 8,
  },
  dragHandle: {
    position: "absolute",
    right: 0,
    top: 0,
    bottom: 0,
    flexDirection: "row",
    width: 20,
    justifyContent: "center",
    gap: 2,
    alignItems: "center",
    marginLeft: 8,
    backgroundColor: "#ccc",
  },
  dragDots: {
    justifyContent: "center",
    height: 12,
    gap: 2,
  },
  dot: {
    width: 3,
    height: 3,
    borderRadius: 1.5,
    backgroundColor: "#9CA3AF",
    marginVertical: 1,
  },
});

export default ActivityCard;
